/**
 * Sound Manager Service
 * Howler.js wrapper for loading, caching, and playing creature sounds
 * @module services/sound/sound-manager
 */

import { Howl, Howler } from 'howler';
import {
  SoundType,
  CreatureSoundUrls,
  SoundManagerConfig,
  DEFAULT_SOUND_CONFIG,
  CreatureSoundType,
} from './types';
import { gsUrlToHttps } from './url-converter';
import { SHARED_SOUND_URLS, SharedSoundKey } from './shared-sounds';

/**
 * Sound Manager for loading and playing creature sounds
 *
 * Features:
 * - Loads sounds from Firebase Storage URLs
 * - Caches loaded sounds to prevent re-downloading
 * - Volume ducking when multiple sounds play simultaneously
 * - Mobile audio unlock handling (iOS/Android)
 * - Shared spawn/death sounds for all creatures
 * - Fallback sounds for creatures without generated audio
 */
export class SoundManager {
  private config: SoundManagerConfig;
  private soundCache: Map<string, Howl> = new Map();
  private creatureSounds: Map<string, CreatureSoundUrls> = new Map();
  private sharedSounds: Map<string, Howl> = new Map();
  private activeSoundIds: Set<number> = new Set();
  private audioUnlocked = false;
  private initialized = false;

  constructor(config?: Partial<SoundManagerConfig>) {
    this.config = { ...DEFAULT_SOUND_CONFIG, ...config };
    this.setupMobileUnlock();
  }

  /**
   * Preload shared sounds (spawn, death, fallbacks)
   * Call this once at app startup or before first combat
   */
  async preloadSharedSounds(): Promise<void> {
    if (this.initialized) return;

    console.log('[SoundManager] Preloading shared sounds...');

    const loadPromises = Object.entries(SHARED_SOUND_URLS).map(
      async ([key, url]) => {
        try {
          const howl = await this.loadSound(url);
          this.sharedSounds.set(key, howl);
          console.log(`[SoundManager] Loaded shared sound: ${key}`);
        } catch (error) {
          console.warn(`[SoundManager] Failed to load shared sound ${key}:`, error);
        }
      }
    );

    await Promise.allSettled(loadPromises);
    this.initialized = true;
    console.log('[SoundManager] Shared sounds preloaded');
  }

  /**
   * Preload all sounds for a creature
   * @param creatureId - Unique creature ID
   * @param soundUrls - Object containing sound URLs from CreatureDocument.sounds
   */
  async preloadCreatureSounds(
    creatureId: string,
    soundUrls: CreatureSoundUrls
  ): Promise<void> {
    this.creatureSounds.set(creatureId, soundUrls);

    const urlsToLoad: string[] = [];

    if (soundUrls.attack) urlsToLoad.push(soundUrls.attack);
    if (soundUrls.hit) urlsToLoad.push(soundUrls.hit);
    if (soundUrls.projectileLaunch) urlsToLoad.push(soundUrls.projectileLaunch);
    if (soundUrls.projectileImpact) urlsToLoad.push(soundUrls.projectileImpact);

    if (urlsToLoad.length === 0) {
      console.log(`[SoundManager] No sounds to preload for creature ${creatureId}`);
      return;
    }

    console.log(`[SoundManager] Preloading ${urlsToLoad.length} sounds for creature ${creatureId}`);

    const loadPromises = urlsToLoad.map((url) =>
      this.loadSound(url).catch((error) => {
        console.warn(`[SoundManager] Failed to load sound ${url}:`, error);
        return null;
      })
    );

    await Promise.allSettled(loadPromises);
  }

  /**
   * Load a single sound from URL
   * Returns cached sound if already loaded
   */
  private loadSound(gsUrl: string): Promise<Howl> {
    // Check cache first
    const cached = this.soundCache.get(gsUrl);
    if (cached) {
      return Promise.resolve(cached);
    }

    const httpsUrl = gsUrlToHttps(gsUrl);

    return new Promise((resolve, reject) => {
      const howl = new Howl({
        src: [httpsUrl],
        format: ['mp3'],
        preload: true,
        html5: false, // Use Web Audio API for better performance
        onload: () => {
          this.soundCache.set(gsUrl, howl);
          resolve(howl);
        },
        onloaderror: (_id, error) => {
          console.warn(`[SoundManager] Load error for ${gsUrl}:`, error);
          reject(error);
        },
      });
    });
  }

  /**
   * Play a creature-specific sound
   * @param creatureId - Creature ID
   * @param soundType - Type of sound to play
   * @returns Sound ID for control, or null if sound not available
   */
  playSound(creatureId: string, soundType: CreatureSoundType): number | null {
    const urls = this.creatureSounds.get(creatureId);

    // Get the URL for this sound type
    let url: string | undefined;
    if (urls) {
      url = urls[soundType];
    }

    // Fall back to default sounds if creature doesn't have this sound
    if (!url) {
      if (soundType === 'attack') {
        return this.playSharedSound('defaultAttack');
      }
      if (soundType === 'hit') {
        return this.playSharedSound('defaultHit');
      }
      console.warn(`[SoundManager] No ${soundType} sound for creature ${creatureId}`);
      return null;
    }

    const howl = this.soundCache.get(url);
    if (!howl) {
      console.warn(`[SoundManager] Sound not cached: ${url}`);
      return null;
    }

    return this.playSoundWithDucking(howl);
  }

  /**
   * Play a shared sound (spawn, death, defaults)
   * @param soundType - Type of shared sound
   * @returns Sound ID for control, or null if sound not available
   */
  playSharedSound(soundType: SharedSoundKey): number | null {
    const howl = this.sharedSounds.get(soundType);

    if (!howl) {
      console.warn(`[SoundManager] Shared sound not loaded: ${soundType}`);
      return null;
    }

    return this.playSoundWithDucking(howl);
  }

  /**
   * Play a sound with volume ducking applied
   */
  private playSoundWithDucking(howl: Howl): number {
    const volume = this.calculateVolume();
    howl.volume(volume);

    const soundId = howl.play();
    this.activeSoundIds.add(soundId);

    howl.once('end', () => {
      this.activeSoundIds.delete(soundId);
    });

    return soundId;
  }

  /**
   * Calculate current volume based on number of active sounds
   * Applies gradual ducking as more sounds play
   */
  private calculateVolume(): number {
    const activeCount = this.activeSoundIds.size;
    const baseVolume = this.config.masterVolume * this.config.effectsVolume;

    if (activeCount === 0) {
      return baseVolume;
    }

    if (activeCount >= this.config.maxConcurrentSounds) {
      return baseVolume * this.config.duckingFactor;
    }

    // Gradual ducking: reduce volume proportionally as more sounds play
    const duckingRatio = activeCount / this.config.maxConcurrentSounds;
    const duckAmount = duckingRatio * (1 - this.config.duckingFactor);

    return baseVolume * (1 - duckAmount);
  }

  /**
   * Stop a playing sound
   * @param soundId - ID returned from playSound
   */
  stopSound(soundId: number): void {
    // Howler handles finding the sound by ID
    Howler.stop();
    this.activeSoundIds.delete(soundId);
  }

  /**
   * Stop all playing sounds
   */
  stopAllSounds(): void {
    Howler.stop();
    this.activeSoundIds.clear();
  }

  /**
   * Set master volume
   * @param volume - Volume level 0.0 - 1.0
   */
  setMasterVolume(volume: number): void {
    this.config.masterVolume = Math.max(0, Math.min(1, volume));
    Howler.volume(this.config.masterVolume);
  }

  /**
   * Get current master volume
   */
  getMasterVolume(): number {
    return this.config.masterVolume;
  }

  /**
   * Set muted state
   * @param muted - Whether to mute all audio
   */
  setMuted(muted: boolean): void {
    Howler.mute(muted);
  }

  /**
   * Check if audio is muted
   */
  isMuted(): boolean {
    return (Howler as unknown as { _muted: boolean })._muted ?? false;
  }

  /**
   * Check if audio context is unlocked (for mobile)
   */
  isAudioUnlocked(): boolean {
    return this.audioUnlocked;
  }

  /**
   * Set up mobile audio unlock listeners
   * iOS and some Android browsers require user interaction before playing audio
   */
  private setupMobileUnlock(): void {
    const unlockAudio = () => {
      if (this.audioUnlocked) return;

      // Create and play a silent sound to unlock audio context
      const silentSound = new Howl({
        src: [
          'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYNAAAAAAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYNAAAAAAAAAAAAAAAAAAAA',
        ],
        format: ['mp3'],
        volume: 0,
        onend: () => {
          this.audioUnlocked = true;
          console.log('[SoundManager] Audio context unlocked');
        },
      });
      silentSound.play();

      // Remove listeners after first interaction
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('touchend', unlockAudio);
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };

    // Add listeners for various user interactions
    document.addEventListener('touchstart', unlockAudio, { once: true });
    document.addEventListener('touchend', unlockAudio, { once: true });
    document.addEventListener('click', unlockAudio, { once: true });
    document.addEventListener('keydown', unlockAudio, { once: true });
  }

  /**
   * Clean up all sounds and resources
   */
  cleanup(): void {
    this.stopAllSounds();
    this.soundCache.clear();
    this.creatureSounds.clear();
    this.sharedSounds.clear();
    this.activeSoundIds.clear();
    this.initialized = false;
    Howler.unload();
    console.log('[SoundManager] Cleanup complete');
  }
}
